import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Heading, Input, Label, Paragraph, Select, ValidationMessage } from '@digdir/designsystemet-react';
import type { TemplateLanguage, TemplateLanguageView } from '../../../../api/hms';
import { languageName, languageNameInText, MAIN_LANGUAGE } from '../../hmsLanguages';
import {
  addableLanguages,
  type NewLanguage,
  newLanguageError,
  OTHER_LANGUAGE,
  progressPercent,
  toNewLanguage,
} from '../languagesView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskFactors.module.css';

/** A language with how much of the template is translated to it */
type LanguageProgress = Pick<TemplateLanguageView, 'code' | 'name' | 'translated' | 'total'>;

type Props = {
  languages: LanguageProgress[];
  /** Languages HR can choose from the list */
  selectable: string[];
  locked: boolean;
  saving: boolean;
  saveFailed: boolean;
  onSave: (languages: TemplateLanguage[], onSaved?: () => void) => void;
  onTranslate: (code: string) => void;
  /** Who sees the languages. The survey's employees unless told otherwise. */
  intro?: string;
};

const EMPTY: NewLanguage = { selected: '', name: '', code: '' };

export const LanguageCard = ({
  languages,
  selectable,
  locked,
  saving,
  saveFailed,
  onSave,
  onTranslate,
  intro,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState<NewLanguage | null>(null);
  const [tried, setTried] = useState(false);
  const current = languages.map(({ code, name }) => ({ code, name }));
  const error = adding ? newLanguageError(adding, current) : null;

  const open = () => {
    setAdding(EMPTY);
    setTried(false);
  };

  const add = () => {
    if (!adding) return;
    if (error) {
      setTried(true);
      return;
    }
    const language = toNewLanguage(adding);
    onSave([...current, language], () => {
      setAdding(null);
      onTranslate(language.code);
    });
  };

  return (
    <section aria-labelledby="hr-rf-languages" className={layout.sideCard}>
      <div className={layout.cardHeading}>
        <Heading level={2} data-size="xs" id="hr-rf-languages">
          {t('hrRiskFactors.languages.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {intro ?? t('hrRiskFactors.languages.intro')}
        </Paragraph>
      </div>
      <ul className={classes.languages}>
        <li>
          <span className="ds-label" data-size="sm">
            {t(`hms.languages.${MAIN_LANGUAGE}`)}
          </span>
          <span className={`ds-paragraph ${layout.subtle}`} data-size="xs">
            {t('hrRiskFactors.languages.main')}
          </span>
        </li>
        {languages.map((language) => {
          const name = languageName(language, t);
          const complete = language.translated === language.total;
          const percent = progressPercent(language);
          return (
            <li key={language.code}>
              <span className={classes.languageRow}>
                <span className="ds-label" data-size="sm" lang={language.code}>
                  {name}
                </span>
                {!locked && (
                  <span className={classes.questionActions}>
                    <Button
                      variant="tertiary"
                      data-size="sm"
                      aria-label={t('hrRiskFactors.languages.translateLabel', { language: languageNameInText(language, t) })}
                      onClick={() => onTranslate(language.code)}
                    >
                      {t('hrRiskFactors.languages.translate')}
                    </Button>
                    <Button
                      variant="tertiary"
                      data-size="sm"
                      aria-label={t('hrRiskFactors.languages.removeLabel', { language: languageNameInText(language, t) })}
                      onClick={() => onSave(current.filter((candidate) => candidate.code !== language.code))}
                    >
                      {t('hrRiskFactors.languages.remove')}
                    </Button>
                  </span>
                )}
              </span>
              <span className="ds-paragraph" data-size="xs" data-complete={complete}>
                {complete
                  ? t('hrRiskFactors.languages.complete', { count: language.total })
                  : t('hrRiskFactors.languages.progress', { translated: language.translated, total: language.total })}
              </span>
              <span aria-hidden="true" className={classes.progress}>
                <span style={{ width: `${percent}%` }} data-complete={complete} />
              </span>
            </li>
          );
        })}
      </ul>
      {adding && (
        <div className={classes.addLanguage}>
          <Field data-size="sm">
            <Label htmlFor="hr-rf-language">{t('hrRiskFactors.languages.language')}</Label>
            <Select
              id="hr-rf-language"
              aria-invalid={(tried && Boolean(error)) || undefined}
              value={adding.selected}
              onChange={(event) => setAdding({ ...adding, selected: event.target.value })}
            >
              <Select.Option value="">{t('hrRiskFactors.languages.choose')}</Select.Option>
              {addableLanguages(selectable, current).map((code) => (
                <Select.Option key={code} value={code}>
                  {languageName({ code, name: null }, t)}
                </Select.Option>
              ))}
              <Select.Option value={OTHER_LANGUAGE}>{t('hrRiskFactors.languages.other')}</Select.Option>
            </Select>
            {tried && error && <ValidationMessage>{t(`hrRiskFactors.languages.errors.${error}`)}</ValidationMessage>}
          </Field>
          {adding.selected === OTHER_LANGUAGE && (
            <div className={classes.otherLanguage}>
              <Field data-size="sm">
                <Label htmlFor="hr-rf-language-name">{t('hrRiskFactors.languages.name')}</Label>
                <Input
                  id="hr-rf-language-name"
                  value={adding.name}
                  onChange={(event) => setAdding({ ...adding, name: event.target.value })}
                />
              </Field>
              <Field data-size="sm">
                <Label htmlFor="hr-rf-language-code">{t('hrRiskFactors.languages.code')}</Label>
                <Input
                  id="hr-rf-language-code"
                  placeholder={t('hrRiskFactors.languages.codePlaceholder')}
                  value={adding.code}
                  onChange={(event) => setAdding({ ...adding, code: event.target.value })}
                />
              </Field>
            </div>
          )}
          <div className={layout.actions} data-gap="sm">
            <Button variant="primary" data-size="sm" onClick={add} loading={saving}>
              {t('hrRiskFactors.languages.add')}
            </Button>
            <Button variant="secondary" data-size="sm" onClick={() => setAdding(null)}>
              {t('hrRiskFactors.cancel')}
            </Button>
          </div>
        </div>
      )}
      {saveFailed && (
        <Alert data-color="danger">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
      {!adding && !locked && (
        <Button
          variant="secondary"
          data-size="sm"
          className={layout.startAligned}
          onClick={open}
        >
          {t('hrRiskFactors.languages.addLanguage')}
        </Button>
      )}
      <Paragraph data-size="xs" className={layout.subtle}>
        {t('hrRiskFactors.languages.footnote')}
      </Paragraph>
    </section>
  );
};
